export interface ReviewedTimeRange {
  start: number;
  end: number;
}

export const EEG_REVIEW_PROGRESS_STORAGE_PREFIX = "eeg-review-progress";

export function buildReviewProgressStorageKey(
  recordingId: string,
  userId: string,
): string {
  return `${EEG_REVIEW_PROGRESS_STORAGE_PREFIX}:${userId}:${recordingId}`;
}

export function mergeReviewedRanges(
  ranges: ReviewedTimeRange[],
  start: number,
  end: number,
): ReviewedTimeRange[] {
  const safeStart = Math.max(0, Math.min(start, end));
  const safeEnd = Math.max(safeStart, end);
  if (safeEnd - safeStart < 0.05) {
    return ranges;
  }

  const next = [...ranges, { start: safeStart, end: safeEnd }].sort(
    (a, b) => a.start - b.start,
  );
  const merged: ReviewedTimeRange[] = [];

  for (const range of next) {
    const last = merged[merged.length - 1];
    if (!last || range.start > last.end + 0.25) {
      merged.push({ ...range });
      continue;
    }
    last.end = Math.max(last.end, range.end);
  }

  return merged;
}

export function readReviewedRanges(
  recordingId: string,
  userId: string,
): ReviewedTimeRange[] {
  if (!recordingId) return [];
  try {
    const raw = localStorage.getItem(
      buildReviewProgressStorageKey(recordingId, userId),
    );
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ReviewedTimeRange[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (range) =>
        Number.isFinite(range.start) &&
        Number.isFinite(range.end) &&
        range.end > range.start,
    );
  } catch {
    return [];
  }
}

export function writeReviewedRanges(
  recordingId: string,
  userId: string,
  ranges: ReviewedTimeRange[],
): void {
  if (!recordingId) return;
  try {
    localStorage.setItem(
      buildReviewProgressStorageKey(recordingId, userId),
      JSON.stringify(ranges),
    );
  } catch {
    // ignore storage errors
  }
}
